import React from 'react'
import styled from '@emotion/styled'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { colors } from '@mobi/component-library/Foundations'
import { store } from '@core/Store'
import { useAppDispatch, useAppSelector } from '@core/Store/hooks'
import { useServerConfig } from '@core/Data/ServerConfig/useServerConfig'
import { ErrorCallbackArgs } from '@core/Utils/hooks/Video/errors'
import { VideoPlayerControls } from './Components/VideoPlayerControls/VideoPlayerControls'
import { handleFullscreenChange, handleVolumeChange, useFullscreen, useLogout } from './helpers'
import { useEntitlement } from './helpers/useEntitlement'
import { Hls, useVideoStream } from '@core/Utils/hooks/Video/useVideoStream'
import skyRacingBackground from './images/logo-sky-racing.png'
import {
  setIsPlaying,
  setCloseSkyVideoPlayer,
  type SkyVideoPlayerState,
  setRaceReplayDurationSeconds,
  setRaceReplayElapsedSeconds,
} from '../../Store'
import { selectSky1SecureUrl, selectSky2SecureUrl } from '../../Store/selectors'
import { fetchAndStoreSkySecureUrl } from '../../helpers/skySecureUrlService'

export const VIDEO_PLAYER_PARENT_ID = 'js-vision-video-player-parent'

export const VideoPlayer = React.memo(() => {
  const videoContainerRef = React.useRef<HTMLDivElement>(null)
  const videoRef = React.useRef<HTMLVideoElement>(null)

  const dispatch = useAppDispatch()
  const isPlaying = useAppSelector(state => state.skyVideoPlayer.isPlaying)
  const isReplay = useAppSelector(state => state.skyVideoPlayer.selectedChannel === 'Replay')

  useEntitlement()

  // Resume play on tab visibility and app foreground
  React.useEffect(() => {
    const playVideo = () => dispatch(setIsPlaying(document.visibilityState === 'visible'))
    document.addEventListener('visibilitychange', playVideo)
    return () => document.removeEventListener('visibilitychange', playVideo)
  }, [dispatch])

  return (
    <VideoPlayerRatioWrapperStyled>
      <VideoPlayerRatioInnerStyled
        ref={videoContainerRef}
        id={VIDEO_PLAYER_PARENT_ID}
        style={{ backgroundImage: `url("${skyRacingBackground}")` }}
      >
        {(isReplay || isPlaying) && (
          <VideoPlayerComponent
            key='video-player'
            parentContainer={videoContainerRef}
            videoRef={videoRef}
          />
        )}

        <VideoPlayerControls parentElRef={videoContainerRef} videoElRef={videoRef} />
      </VideoPlayerRatioInnerStyled>
    </VideoPlayerRatioWrapperStyled>
  )
})

VideoPlayer.displayName = 'VideoPlayer'

// ===============
// Local Component
// ===============

const VideoPlayerComponent: React.FC<{
  parentContainer: React.RefObject<HTMLDivElement>
  videoRef: React.RefObject<HTMLVideoElement>
}> = ({ parentContainer, videoRef }) => {
  const dispatch = useAppDispatch()

  const selectedChannel = useAppSelector(state => state.skyVideoPlayer.selectedChannel)
  const isMuted = useAppSelector(state => state.skyVideoPlayer.isMuted)
  const isFullscreen = useAppSelector(state => state.skyVideoPlayer.isFullscreen)
  const raceReplayUrl = useAppSelector(state => state.skyVideoPlayer.raceReplayUrl)
  const raceReplayUrlChanged = useAppSelector(state => state.skyVideoPlayer.raceReplayUrlChanged)
  const raceReplayElapsedSeconds = useAppSelector(
    state => state.skyVideoPlayer.raceReplayElapsedSeconds
  )
  const sky1SecureUrl = useAppSelector(selectSky1SecureUrl)
  const sky2SecureUrl = useAppSelector(selectSky2SecureUrl)

  const racingWAStreamUrl = useServerConfig()?.racingWAStreamUrl
  const isReplay = selectedChannel === 'Replay'

  // Fetch secure URLs when they're missing (pre-fetch both for instant channel switching)
  React.useEffect(() => {
    if (!sky1SecureUrl) fetchAndStoreSkySecureUrl('Sky1')
    if (!sky2SecureUrl) fetchAndStoreSkySecureUrl('Sky2')
  }, [sky1SecureUrl, sky2SecureUrl])

  const channelStreams = React.useMemo<Record<SkyVideoPlayerState['selectedChannel'], string>>(
    () => ({
      Sky1: sky1SecureUrl || '',
      Sky2: sky2SecureUrl || '',
      RacingWA: racingWAStreamUrl || '',
      Replay: raceReplayUrl || '',
    }),
    [sky1SecureUrl, sky2SecureUrl, racingWAStreamUrl, raceReplayUrl]
  )

  const [channelStreamUrl, setChannelStreamUrl] = React.useState(channelStreams[selectedChannel])

  useLogout()
  useFullscreen(isFullscreen)

  React.useEffect(() => {
    setChannelStreamUrl(channelStreams[selectedChannel])
  }, [channelStreams, selectedChannel])

  // initialise the playback position when the video during the first render, e.g. allow transition from docked/undocked/fullscreen
  React.useEffect(() => {
    if (videoRef?.current) videoRef.current.currentTime = raceReplayElapsedSeconds
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // reset the playback position whenever the URL changes
  React.useEffect(() => {
    if (videoRef?.current && raceReplayUrlChanged) videoRef.current.currentTime = 0
  }, [raceReplayUrlChanged, videoRef])

  React.useEffect(() => {
    const videoEl = videoRef.current
    if (!videoEl || !isReplay) return undefined

    const handleTimeUpdate = () => {
      if (!videoRef?.current || isNaN(videoRef.current.currentTime)) return
      dispatch(setRaceReplayElapsedSeconds(videoRef.current.currentTime))
    }
    videoEl.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      videoEl.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [videoRef, isReplay, dispatch])

  useVideoStream({ videoRef, streamUrl: channelStreamUrl, onError, isStream: !isReplay })

  React.useEffect(() => {
    const videoEl = videoRef.current
    const videoParentEl = parentContainer.current
    if (!videoEl || !videoParentEl) return undefined

    videoEl.setAttribute('webkit-playsinline', '')
    videoEl.setAttribute('x5-playsinline', '')
    videoEl.setAttribute('disableRemotePlayback', '')

    const playVideo = () => {
      dispatch(setRaceReplayDurationSeconds(videoEl.duration))
      dispatch(setIsPlaying(true))
    }
    const stopVideo = () => {
      dispatch(setIsPlaying(false))
    }
    let stopTimerId: number
    const stopVideoWithDelay = () => {
      stopTimerId = window.setTimeout(stopVideo, 300)
    }

    videoEl.addEventListener('play', playVideo)
    videoEl.addEventListener('pause', stopVideoWithDelay)
    videoEl.addEventListener('ended', stopVideo)
    videoEl.addEventListener('abort', stopVideo)
    videoEl.addEventListener('volumechange', handleVolumeChange)

    videoParentEl.addEventListener('fullscreenchange', handleFullscreenChange)
    videoParentEl.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    if (videoEl.webkitSupportsPresentationMode) {
      videoEl.addEventListener('webkitpresentationmodechanged', handleFullscreenChange)
    }

    return () => {
      videoEl.removeEventListener('play', playVideo)
      videoEl.removeEventListener('pause', stopVideoWithDelay)
      videoEl.removeEventListener('ended', stopVideo)
      videoEl.removeEventListener('abort', stopVideo)
      videoEl.removeEventListener('volumechange', handleVolumeChange)

      videoParentEl.removeEventListener('fullscreenchange', handleFullscreenChange)
      videoParentEl.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      if (videoEl.webkitSupportsPresentationMode) {
        videoEl.removeEventListener('webkitpresentationmodechanged', handleFullscreenChange)
      }

      videoEl.pause()
      if (stopTimerId) clearTimeout(stopTimerId)
    }
  }, [videoRef, parentContainer, dispatch])

  return (
    <video
      ref={videoRef}
      style={{ flex: 1, maxHeight: '100%', width: '100%' }}
      preload='auto'
      autoPlay
      playsInline
      controls={false}
      muted={isMuted}
      disablePictureInPicture
    />
  )
}

// =============
// Local Helpers
// =============

function onError(error: ErrorCallbackArgs, hlsInstance?: Hls) {
  if (error.type !== 'hlsError') return
  const { type, fatal, details } = error.error
  if (!fatal || !hlsInstance) return

  switch (type) {
    case Hls.ErrorTypes.NETWORK_ERROR:
      // Manifest load error if access to Sky Vision stream returns 403
      if (details !== Hls.ErrorDetails.MANIFEST_LOAD_ERROR) {
        hlsInstance.startLoad()
      } else {
        store.dispatch(setCloseSkyVideoPlayer())
        addToast({
          id: 'VisionNotEntitled',
          title: 'An unexpected error occurred. Please refresh the page and try again.',
          icon: 'error',
          timeoutMs: Infinity,
        })
      }
      break
    case Hls.ErrorTypes.MEDIA_ERROR:
    case Hls.ErrorTypes.OTHER_ERROR:
      hlsInstance.recoverMediaError()
      break
    default:
      store.dispatch(setCloseSkyVideoPlayer())
      break
  }
}

// ======
// Styles
// ======

const enum LocalConstants {
  AspectRatio = '16 / 9',
}

const VideoPlayerRatioWrapperStyled = styled.div({
  position: 'relative',
  overflow: 'hidden',

  height: 0,
  paddingTop: 'min(56.25%, 100vh)', // 16:9

  '@supports (aspect-ratio: 16 / 9)': {
    height: 'auto',
    aspectRatio: LocalConstants.AspectRatio,
    paddingTop: 0,
  },
})

const VideoPlayerRatioInnerStyled = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  aspectRatio: LocalConstants.AspectRatio,
  height: '100%',
  margin: '0 auto',
  backgroundSize: 'contain',
  backgroundColor: colors.black,
})
